const CTA_EVENT_RULES = [
  {
    eventName: "apply_click",
    patterns: ["apply", "apply online", "registration", "register", "application"],
    keys: ["apply_online", "registration", "apply", "official_application_link"],
  },
  {
    eventName: "result_click",
    patterns: ["result", "scorecard", "merit list"],
    keys: ["result_link", "download_result", "view_result", "check_result"],
  },
  {
    eventName: "admit_card_click",
    patterns: ["admit card", "hall ticket", "exam city", "intimation"],
    keys: ["admit_card", "download_admit_card", "exam_city", "intimation"],
  },
  {
    eventName: "notification_download_click",
    patterns: ["notification", "advertisement", "notice", "pdf"],
    keys: ["notification_pdf", "check_official_notification", "official_notification", "advertisement_pdf"],
  },
  {
    eventName: "official_website_click",
    patterns: ["official website", "official site", "main website", "official portal"],
    keys: ["official_website", "source", "source_link", "website"],
  },
  {
    eventName: "answer_key_click",
    patterns: ["answer key"],
    keys: ["answer_key", "download_answer_key", "view_answer_key"],
  },
  {
    eventName: "syllabus_click",
    patterns: ["syllabus"],
    keys: ["syllabus", "download_syllabus", "view_syllabus"],
  },
];

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const safeWindow = () => (typeof window !== "undefined" ? window : null);

export const getPageType = (pathname = "") => {
  const path = String(pathname || "").toLowerCase();

  if (path.startsWith("/job/")) return "job_detail";
  if (path === "/results" || path === "/admit-card" || path === "/latest-jobs" || path === "/answer-key") {
    return "listing_page";
  }
  if (path.startsWith("/syllabus")) return "exam_page";

  return "other";
};

export const parseDestinationDomain = (url = "") => {
  const win = safeWindow();
  if (!url) return "";

  try {
    const parsed = win ? new URL(url, win.location.origin) : new URL(url);
    return parsed.hostname || "";
  } catch {
    return "";
  }
};

export const isExternalDestination = (url = "") => {
  const win = safeWindow();
  if (!win || !url) return false;

  try {
    const parsed = new URL(url, win.location.origin);
    return parsed.origin !== win.location.origin;
  } catch {
    return false;
  }
};

export const inferEventNameFromCta = ({ fieldKey = "", label = "", destinationUrl = "" }) => {
  const haystack = [fieldKey, label, destinationUrl].map(normalizeText).join(" ");

  const match = CTA_EVENT_RULES.find((rule) => {
    const patternMatch = rule.patterns.some((pattern) => haystack.includes(normalizeText(pattern)));
    const keyMatch = rule.keys.some((key) => haystack.includes(normalizeText(key)));
    return patternMatch || keyMatch;
  });

  return match?.eventName || "";
};

export const trackEvent = (eventName, payload = {}) => {
  const win = safeWindow();
  if (!win || typeof win.gtag !== "function" || !eventName) return;

  const pagePath = payload.page_path || win.location.pathname || "";
  const pageUrl = payload.page_url || win.location.href || "";
  const destinationUrl = payload.destination_url || "";
  const destinationDomain = payload.destination_domain || parseDestinationDomain(destinationUrl);

  try {
    win.gtag("event", eventName, {
      page_type: payload.page_type || getPageType(pagePath),
      page_path: pagePath,
      page_url: pageUrl,
      page_title: payload.page_title || document.title || "",
      item_title: payload.item_title || "",
      destination_url: destinationUrl,
      destination_domain: destinationDomain,
      source_section: payload.source_section || "",
      exam_name: payload.exam_name || "",
      organization_name: payload.organization_name || "",
      post_name: payload.post_name || "",
      category_type: payload.category_type || "",
      is_external_link:
        typeof payload.is_external_link === "boolean"
          ? payload.is_external_link
          : isExternalDestination(destinationUrl),
    });
  } catch {
    // Never break UX for analytics failures.
  }
};

export const trackCtaClick = ({
  eventName,
  fieldKey = "",
  label = "",
  destinationUrl = "",
  ...payload
}) => {
  const resolvedEventName = eventName || inferEventNameFromCta({ fieldKey, label, destinationUrl });
  if (!resolvedEventName) return;

  trackEvent(resolvedEventName, {
    ...payload,
    destination_url: destinationUrl,
  });
};

export const trackApplyClick = (payload) => trackEvent("apply_click", payload);
export const trackResultClick = (payload) => trackEvent("result_click", payload);
export const trackAdmitCardClick = (payload) => trackEvent("admit_card_click", payload);
export const trackNotificationDownloadClick = (payload) =>
  trackEvent("notification_download_click", payload);
export const trackOfficialWebsiteClick = (payload) =>
  trackEvent("official_website_click", payload);
export const trackAnswerKeyClick = (payload) => trackEvent("answer_key_click", payload);
export const trackSyllabusClick = (payload) => trackEvent("syllabus_click", payload);
